import React, { useCallback, useState } from "react";
import {
  View,
  SectionList,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import { getDsReturns } from "@services/api/dsRouting";
import RoutingHeader from "../components/RoutingHeader";
import RoutingSearch from "../components/RoutingSearch";
import { useRoutingSearch } from "../useRoutingSearch";
import OptionSheet from "../components/OptionSheet";
import RerackSheet from "../components/RerackSheet";
import {
  rerackProgress,
  rerackStateLook,
  buildReturnSections,
  returnsDbKey,
  RETURNS_ALL_DBS,
} from "../helpers";
import { C, RText, Tag, ChevronDown, AddressLine, tpName } from "../theme";

const PATH = "DsRouting/returns";

const TONE = {
  amber: { bg: C.amberBg, color: C.amber },
  blue: { bg: C.blueBg, color: C.blue },
  red: { bg: C.chipRedBg, color: C.chipRed },
};

const plural = (count, one, many) =>
  t(`${PATH}/${count === 1 ? one : many}`, { count: String(count) });

// Registered as `DsReturnsList`; opened from the hub's "Returned to store"
// card. Boxes that went out with a DB, aren't delivered and aren't on a rack,
// grouped by delivery boy (one section each) with a delivery-boy filter.
// Re-rack opens the scan sheet. Works any time of day: no wait for recon.
const DsReturnsList = ({ navigation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dbFilter, setDbFilter] = useState(RETURNS_ALL_DBS);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [active, setActive] = useState(null);
  const search = useRoutingSearch();

  const load = useCallback(async ({ pull = false } = {}) => {
    try {
      if (pull) setRefreshing(true);
      else setLoading(true);
      const response = await getDsReturns();
      setData(response?.data?.data || null);
    } catch (error) {
      showApiError(error, () => "DsRouting/errorGeneric");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const dbs = data?.dbs || [];
  const options = [
    {
      key: RETURNS_ALL_DBS,
      label: t(`${PATH}/allDbs`),
      count: data ? data.boxes_to_rerack || 0 : null,
    },
  ].concat(
    dbs.map((db) => ({
      key: returnsDbKey(db),
      label: db.name || t(`${PATH}/noDb`),
      count: db.to_rerack || 0,
    })),
  );
  const selected =
    options.find((option) => option.key === dbFilter) || options[0];
  const sections = buildReturnSections(dbs, selected.key, search.query);

  const closeSheet = () => {
    setActive(null);
    load();
  };

  const renderFilter = () => (
    <View style={styles.sticky}>
      <Pressable
        testID="ds-returns-db-filter"
        onPress={() => setSheetOpen(true)}
        style={[
          styles.dropdown,
          selected.key !== RETURNS_ALL_DBS && styles.dropdownActive,
        ]}
      >
        <RText
          size={12}
          weight={600}
          color={selected.key !== RETURNS_ALL_DBS ? C.chipRed : C.ink}
          numberOfLines={1}
          style={styles.shrink}
        >
          {selected.label}
        </RText>
        {selected.count != null ? (
          <View style={styles.bubble}>
            <RText size={10} weight={700} color={C.surface}>
              {String(selected.count)}
            </RText>
          </View>
        ) : null}
        <View style={styles.grow} />
        <ChevronDown
          color={selected.key !== RETURNS_ALL_DBS ? C.chipRed : C.muted}
        />
      </Pressable>
      <RText size={12} weight={500} color={C.muted} style={styles.helper}>
        {t(`${PATH}/helper`)}
      </RText>
    </View>
  );

  const renderSectionHeader = ({ section: { db, key } }) => {
    const progress = rerackProgress(db);
    const meta = [
      plural(db.customers?.length || 0, "customer", "customers"),
      db.on_trip
        ? t(`${PATH}/onTripCount`, { count: String(db.on_trip) })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const done = db.to_rerack === 0;
    return (
      <View style={styles.section} testID={`ds-returns-db-${key}`}>
        <View style={styles.top}>
          <View style={styles.grow}>
            <RText size={15} weight={700} numberOfLines={1}>
              {db.name || t(`${PATH}/noDb`)}
            </RText>
            <RText size={11.5} weight={500} color={C.muted} style={styles.meta}>
              {meta}
            </RText>
          </View>
          {done ? (
            <Tag label={t(`${PATH}/done`)} bg={C.greenBg} color={C.green} />
          ) : null}
        </View>
        <View style={styles.track}>
          <View style={[styles.bar, { width: `${progress.pct}%` }]} />
        </View>
        <RText size={11.5} weight={500} color={C.muted}>
          {t(`${PATH}/progress`, {
            done: String(progress.done),
            total: String(progress.total),
          })}
        </RText>
      </View>
    );
  };

  const renderItem = ({ item }) => {
    const look = rerackStateLook(item.state);
    const tone = TONE[look.tone];
    return (
      <View style={styles.card} testID={`ds-returns-cx-${item.cx_id}`}>
        <View style={styles.top}>
          <RText size={15} weight={700} style={styles.grow} numberOfLines={1}>
            {tpName(item)}
          </RText>
          <Tag
            label={t(`${PATH}/state/${look.key}`)}
            bg={tone.bg}
            color={tone.color}
          />
        </View>
        <AddressLine address={item.address} />
        <View style={styles.top}>
          <RText size={11.5} weight={500} color={C.muted} style={styles.grow}>
            {plural(item.boxes.length, "box", "boxes")}
          </RText>
          {item.on_trip ? (
            <Tag label={t(`${PATH}/onTrip`)} bg={C.blueBg} color={C.blue} />
          ) : null}
        </View>
        <Pressable
          testID={`ds-returns-rerack-${item.cx_id}`}
          onPress={() => setActive(item)}
          style={styles.btn}
        >
          <RText size={14.5} weight={600} color="#FFFFFF">
            {t(`${PATH}/rerack`)}
          </RText>
        </Pressable>
      </View>
    );
  };

  return (
    <View style={styles.screen}>
      <RoutingHeader title={t(`${PATH}/title`)} navigation={navigation} />
      <RoutingSearch
        testID="ds-returns-search"
        value={search.text}
        onChangeText={search.setText}
        placeholder={t("DsRouting/searchPlaceholder")}
      />
      {loading && !data ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <SectionList
          testID="ds-returns-list"
          sections={sections}
          keyExtractor={(item, index) => `${item.cx_id}-${index}`}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          ListHeaderComponent={renderFilter}
          stickySectionHeadersEnabled={false}
          refreshing={refreshing}
          onRefresh={() => load({ pull: true })}
          contentContainerStyle={styles.body}
          ListEmptyComponent={
            <RText size={14} weight={500} color={C.muted} style={styles.empty}>
              {t(search.query ? "DsRouting/noMatch" : `${PATH}/empty`)}
            </RText>
          }
        />
      )}
      <OptionSheet
        visible={sheetOpen}
        title={t(`${PATH}/filterTitle`)}
        options={options}
        selected={selected.key}
        onSelect={(key) => {
          setDbFilter(key);
          setSheetOpen(false);
        }}
        onClose={() => setSheetOpen(false)}
        testID="ds-returns-db-options"
      />
      <RerackSheet customer={active} onClose={closeSheet} onDone={closeSheet} />
    </View>
  );
};

export default DsReturnsList;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  body: { paddingHorizontal: 16, paddingBottom: 24, gap: 10 },
  sticky: { paddingTop: 12, gap: 10 },
  dropdown: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 13,
  },
  dropdownActive: { backgroundColor: C.chipRedBg, borderColor: C.chipRed },
  bubble: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: C.muted,
  },
  helper: { lineHeight: 18 },
  section: { paddingTop: 14, gap: 6 },
  top: { flexDirection: "row", alignItems: "center", gap: 8 },
  grow: { flex: 1 },
  shrink: { flexShrink: 1 },
  meta: { paddingTop: 2 },
  track: {
    height: 6,
    backgroundColor: C.track,
    borderRadius: 4,
    overflow: "hidden",
  },
  bar: { height: "100%", backgroundColor: C.green, borderRadius: 4 },
  card: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    gap: 6,
  },
  btn: {
    backgroundColor: C.red,
    borderRadius: 9,
    paddingVertical: 11,
    alignItems: "center",
    marginTop: 6,
  },
  empty: { textAlign: "center", marginTop: 60 },
});
